/**
 * Single source of truth for the technician interface's Vuetify token boundary.
 *
 * Values document the source or derivation. The bridge uses the bucket names
 * to decide which tokens it may emit; tests compare this data with Vuetify's
 * installed defaults so upstream additions cannot cross the boundary silently.
 */
export const INTERFACE_THEME_TOKEN_CONTRACT = {
  colors: {
    INTERFACE_OWNED: {
      primary: '--tw-primary: preset brand identity.',
      accent: '--tw-accent: preset call-to-action colour.',
      background: '--tw-background: preset page canvas.',
      surface: '--tw-surface: preset component surface.',
      'surface-variant': '--tw-surface-variant or --tw-divider: preset neutral surface.',
      success: '--tw-success: preset positive semantic colour.',
      warning: '--tw-warning: preset caution semantic colour.',
      error: '--tw-error: preset destructive semantic colour.',
      info: '--tw-info: preset informational semantic colour.',
    },
    DERIVED: {
      'surface-bright': 'Surface on light themes; lightenHex(surface, 0.12) on dark themes.',
      'surface-light': 'darkenHex(surface, 0.06) on light; lightenHex(surface, 0.06) on dark.',
      'primary-darken-1': 'darkenHex(primary): pressed brand shade.',
      secondary: 'accent: tonal actions share the preset action colour.',
      'secondary-darken-1': 'darkenHex(secondary): pressed tonal shade.',
      'on-primary': 'readableOn(primary, --tw-on-primary-accent).',
      'on-accent': 'readableOn(accent, --tw-on-accent).',
      'on-secondary': 'on-accent: secondary aliases the accent colour.',
      'on-background': 'readableOn(background, --tw-text-primary).',
      'on-surface': 'readableOn(surface, --tw-text-primary).',
      'on-surface-variant': 'readableOn(surface-variant, --tw-text-secondary).',
      'on-error': 'readableOn(error): accessible destructive foreground.',
      'on-success': 'readableOn(success): accessible positive foreground.',
      'on-warning': 'readableOn(warning): accessible caution foreground.',
      'on-info': 'readableOn(info): accessible informational foreground.',
    },
    OFFICE_INHERITED: {},
  },
  variables: {
    INTERFACE_OWNED: {
      'border-color': '--tw-divider or --tw-surface-variant: preset divider colour.',
    },
    DERIVED: {
      'border-opacity': '1 when border-color is preset-owned and already composited.',
    },
    OFFICE_INHERITED: {
      'high-emphasis-opacity': 'Office accessibility emphasis, not preset identity.',
      'medium-emphasis-opacity': 'Office accessibility emphasis, not preset identity.',
      'disabled-opacity': 'Office disabled-state affordance, not preset identity.',
      'idle-opacity': 'Office interaction affordance, not preset identity.',
      'hover-opacity': 'Office interaction affordance, not preset identity.',
      'focus-opacity': 'Office interaction affordance, not preset identity.',
      'selected-opacity': 'Office interaction affordance, not preset identity.',
      'activated-opacity': 'Office interaction affordance, not preset identity.',
      'pressed-opacity': 'Office interaction affordance, not preset identity.',
      'dragged-opacity': 'Office interaction affordance, not preset identity.',
      'theme-kbd': 'Office keyboard-key surface, not preset identity.',
      'theme-on-kbd': 'Office keyboard-key foreground, not preset identity.',
      'theme-code': 'Office code surface, not preset identity.',
      'theme-on-code': 'Office code foreground, not preset identity.',
      'theme-on-surface-variant': 'Office dark-theme compatibility variable.',
    },
  },
}
